"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { FullScreenMenu } from "@/components/layout/full-screen-menu";
import type { SiteSettings, NavigationItem } from "@/types/database";

interface SiteHeaderProps {
  settings: SiteSettings | null;
  headerNav: NavigationItem[];
  menuItems: NavigationItem[];
}

export function SiteHeader({ settings, headerNav, menuItems }: SiteHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Determine if we are past the initial hero section (threshold of 100px for style change)
      const scrolled = currentScrollY > 50;
      setIsScrolled(scrolled);

      // Smart Navbar Logic:
      // 1. If at top (within hero threshold, e.g., 500px), always visible.
      // 2. If scrolling UP, show.
      // 3. If scrolling DOWN and past hero, hide.

      if (currentScrollY < 500) {
        setIsVisible(true);
      } else {
        if (currentScrollY > lastScrollY) {
          // Scrolling Down
          setIsVisible(false);
        } else {
          // Scrolling Up
          setIsVisible(true);
        }
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <>
      <FullScreenMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        menuItems={menuItems}
      />

      <header
        className={cn(
          "fixed top-0 left-0 z-50 w-full p-6 md:px-12 md:py-6 transition-all duration-500 transform",
          isScrolled
            ? "bg-primary/90 backdrop-blur-md text-white shadow-lg py-4 md:py-4"
            : "bg-transparent text-white mix-blend-difference",
          isVisible ? "translate-y-0" : "-translate-y-full",
        )}
      >
        <div className="flex flex-col items-center w-full">
          {/* Top Row: Navigation Elements */}
          <div className="grid grid-cols-3 items-center w-full">
            {/* Left: Menu & Whatsapp */}
            <div className="flex items-center gap-8 justify-start">
              <button
                onClick={() => setIsMenuOpen(true)}
                className="flex items-center gap-3 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] hover:opacity-70 transition-opacity group"
              >
                <span>Menu</span>
                <div className="flex gap-1">
                  <div className="w-1 h-1 bg-current rounded-full"></div>
                  <div className="w-1 h-1 bg-current rounded-full"></div>
                  <div className="w-1 h-1 bg-current rounded-full"></div>
                </div>
              </button>
            </div>

            {/* Center: Logo */}
            <div className="flex justify-center">
              <Link href="/" className="flex flex-col items-center group">
                <span className="font-serif text-3xl md:text-5xl font-normal tracking-[0.15em] uppercase text-center leading-none">
                  {settings?.logo_primary ?? "NOVA"}
                </span>
                <span className="font-sans text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-center mt-1">
                  {settings?.logo_secondary ?? "REALTY"}
                </span>
              </Link>
            </div>

            {/* Right: Contact & Button */}
            <div className="flex items-center gap-8 justify-end">
              <span className="hidden lg:block text-[10px] md:text-xs font-bold uppercase tracking-[0.2em]">
                {settings?.phone_header ?? "+971 (4) 879-7271"}
              </span>
              <Button
                asChild
                variant="default"
                className={cn(
                  "hidden sm:flex rounded-xl uppercase text-[10px] font-bold tracking-[0.2em] h-10 px-8 transition-colors",
                  isScrolled
                    ? "bg-background text-primary border border-primary hover:bg-white hover:text-primary transition-all duration-300"
                    : "bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-none",
                )}
              >
                <a href="/contact">
                  {settings?.cta_text ?? "Get In Touch"}
                </a>
              </Button>
              <button
                className="md:hidden p-2"
                onClick={() => setIsMenuOpen(true)}
              >
                <span className="sr-only">Menu</span>
                <div className="w-6 h-0.5 bg-current mb-1.5"></div>
                <div className="w-6 h-0.5 bg-current"></div>
              </button>
            </div>
          </div>

          {/* Bottom Row: Secondary Navigation (Desktop Only) */}
          <div
            className={cn(
              "hidden md:flex justify-center gap-12 mt-6 transition-all duration-500 overflow-hidden",
              // Removed the hiding logic for scroll state - now it stays visible
              isScrolled
                ? "h-auto mt-4 opacity-100"
                : "h-auto opacity-70 hover:opacity-100",
            )}
          >
            {headerNav.map((item) => (
              <Link
                key={item.id}
                href={item.href}
                className="text-[10px] font-bold uppercase tracking-[0.2em] transition-colors hover:text-white"
              >
                {item.label}
              </Link>
            ))}
          </div >
        </div >
      </header >
    </>
  );
}
